import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';

import { isRelativeDate, TraitTypes } from '@candu-ai/segments/types';
import { multiselectStringOperands } from 'dashboard/pages/Segment/components/constants';

import { stripNameDifferentiator } from './nameDifferentiatorUtils';

// TODO: convert this file into TS!!
const DefinitionMapper = (definitionToForm, formToDefinition) => {
  const mapDefaults = ({ object, type, not }) => {
    return {
      object,
      type,
      not: not === 'true' || not === true,
    };
  };

  return {
    definitionToForm(definition) {
      const mappedValues = definitionToForm(definition);
      const defaults = mapDefaults(definition);

      return {
        ...defaults,
        ...mappedValues,
      };
    },

    formToDefinition(form) {
      const mappedValues = formToDefinition(form);
      const defaults = mapDefaults(form);

      return {
        ...defaults,
        ...mappedValues,
      };
    },
  };
};

const InSegmentMemebershipMapper = DefinitionMapper(
  (definition) => {
    return {
      ISR: {
        segmentId: definition.segmentId,
        not: definition.not === true ? 'true' : 'false',
      },
    };
  },
  ({ ISR: { not, segmentId } }) => {
    return {
      not: not === 'true' ? true : undefined,
      segmentId,
    };
  },
);

const getTypeOfDate = (operand) => {
  if (typeof operand === 'number') {
    return 'relative';
  }

  if (new Date(operand).getTime() === 0) {
    return 'infinite';
  }

  return 'absolute';
};

const getOperand = (operation) => {
  if (operation.type.includes('absolute')) {
    return new Date(new Date(operation.operandDate)).toISOString();
  }
  if (operation.type.includes('infinite')) {
    return new Date(0).toISOString();
  }
  return parseFloat(operation.operand);
};

const EventOccurredMapper = DefinitionMapper(
  ({ eventName = '', trackingEventType = 'Global', not, operation }) => {
    const operationTypePrefix = not ? 'not_' : '';

    const typeOfDate = getTypeOfDate(operation.operand);

    const formOperation = {
      type: `${operationTypePrefix}${typeOfDate}`,
    };

    if (typeOfDate === 'absolute') {
      formOperation.operandDate = new Date(operation.operand);
    } else if (typeOfDate === 'infinite') {
      formOperation.operandDate = new Date(0);
    } else {
      formOperation.operand = operation.operand;
    }

    return {
      EventOccurred: {
        eventName,
        trackingEventType,
        operation: formOperation,
      },
    };
  },
  ({ EventOccurred: { operation, eventName, trackingEventType = 'Global' } }) => {
    const operand = getOperand(operation);

    const definitionOperation = {
      type: '>=',
      operand,
    };

    return {
      eventName,
      trackingEventType,
      type: 'EventOccurred',
      operation: definitionOperation,
      not: operation.type.includes('not'),
    };
  },
);

const UserSeenMapper = DefinitionMapper(
  ({ userSeenRuleType, not, operation }) => {
    const operationTypePrefix = not ? 'not_' : '';
    const typeOfDate = typeof operation.operand === 'number' ? 'relative' : 'absolute';

    const formOperation = {
      type: `${operationTypePrefix}${typeOfDate}`,
    };

    if (typeOfDate === 'absolute') {
      formOperation.operandDate = new Date(operation.operand);
    } else {
      formOperation.operand = operation.operand;
    }

    return {
      type: userSeenRuleType,
      UserSeen: {
        operation: formOperation,
      },
    };
  },
  ({ type, UserSeen: { operation } }) => {
    const operand = operation.type.includes('absolute')
      ? new Date(operation.operandDate).toISOString()
      : parseFloat(operation.operand);

    return {
      userSeenRuleType: type,
      type: 'UserSeen',
      operation: {
        type: '>=',
        operand,
      },
      not: operation.type.includes('not'),
    };
  },
);

const LimitedTimeContentMapper = DefinitionMapper(
  ({ operation }) => {
    const formOperation = {
      type: operation.type || '>=',
      operand: new Date(operation.operand),
    };

    return {
      type: 'LimitedTimeContent',
      LimitedTimeContent: {
        operation: formOperation,
      },
    };
  },
  ({ LimitedTimeContent: { operation } }) => {
    return {
      type: 'LimitedTimeContent',
      operation,
      not: false,
    };
  },
);

export const isISOString = (val) => {
  const d = new Date(val);
  return !Number.isNaN(d.valueOf()) && d.toISOString() === val;
};

const getOperandType = (operand) => {
  if (isBoolean(operand)) {
    return TraitTypes.Boolean;
  }
  if (!isNumber(operand) && isISOString(operand)) {
    return TraitTypes.Date;
  }
  if (isNumber(operand)) {
    return TraitTypes.Number;
  }
  return TraitTypes.String;
};

const UserPropertyMapper = DefinitionMapper(
  ({ operation, ...rest }) => {
    const traitType = getOperandType(operation?.operand);
    let operand = operation?.operand;

    if (traitType === TraitTypes.Date) {
      operand = new Date(operand);
    } else if (multiselectStringOperands.indexOf(operation.type) === -1 && !isNumber(operand)) {
      operand = operand?.toString();
    }

    const ruleTraitType = isRelativeDate(operation.type) ? TraitTypes.Date : traitType;

    return {
      UserProperty: {
        operation: {
          ...operation,
          operand,
          type: rest.not ? `not_${operation.type}` : operation.type,
        },
        traitType: ruleTraitType,
      },
      ...rest,
      traitName: rest.traitName ? `${rest.traitName}_differentiator(${ruleTraitType})` : undefined,
    };
  },
  ({
    traitName,
    UserProperty: {
      operation: { type, operand, setId },
      traitType,
    },
  }) => {
    const parsers = {
      [TraitTypes.Number]: (value) => Number(value),
      [TraitTypes.Boolean]: (value) => value === 'true',
      [TraitTypes.String]: (value) =>
        value || (multiselectStringOperands.indexOf(type) > -1 ? [] : ''),
      [TraitTypes.Date]: (value) => {
        const operandType = getOperandType(value);
        if (operandType === TraitTypes.Date) {
          return new Date(value).toISOString();
        }
        if (operandType === TraitTypes.Number) {
          // treat as number trait type if relative date
          return Number(value);
        }
        // treat as string trait type if undefined
        return value || '';
      },
    };

    const parser = parsers[traitType] || parsers[TraitTypes.String];

    let parsedOperand;
    if (!setId) {
      parsedOperand = parser(operand);
    }

    return {
      not: type.includes('not'),
      operation: { type: type.replace('not_', ''), operand: parsedOperand, setId },
      traitName: traitName ? stripNameDifferentiator(traitName) : undefined,
    };
  },
);

const UserIdMapper = DefinitionMapper(
  ({ operation, ...rest }) => {
    return {
      UserId: {
        operation: {
          ...operation,
          type: rest.not ? `not_${operation.type}` : operation.type,
        },
      },
      ...rest,
    };
  },
  ({ UserId: { operation } }) => {
    const definitionOperation = {
      ...operation,
      type: operation.type.replace('not_', ''),
      operand:
        multiselectStringOperands.indexOf(operation.type) > -1
          ? operation.operand
          : operation.operand.toString(),
    };

    return {
      not: operation.type.includes('not'),
      operation: definitionOperation,
    };
  },
);

export const mappers = {
  UserId: UserIdMapper,
  UserSeen: UserSeenMapper,
  LimitedTimeContent: LimitedTimeContentMapper,
  UserProperty: UserPropertyMapper,
  EventOccurred: EventOccurredMapper,
  ISR: InSegmentMemebershipMapper,
};
